import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'

interface LoadingContextType {
  isLoading: boolean
  loadingStates: Record<string, boolean>
  startLoading: (key: string) => void
  stopLoading: (key: string) => void
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined)

interface LoadingProviderProps {
  children: ReactNode
}

export const LoadingProvider = ({ children }: LoadingProviderProps) => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  )

  const startLoading = useCallback((key: string) => {
    setLoadingStates((prev) => ({ ...prev, [key]: true }))
  }, [])

  const stopLoading = useCallback((key: string) => {
    setLoadingStates((prev) => {
      const newState = { ...prev }
      delete newState[key]
      return newState
    })
  }, [])

  const isLoading = Object.values(loadingStates).some((state) => state)

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        loadingStates,
        startLoading,
        stopLoading,
      }}
    >
      {children}
    </LoadingContext.Provider>
  )
}

export const useLoading = () => {
  const context = useContext(LoadingContext)
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider')
  }
  return context
}
